<template>
  <div>
    <div class="detail-panels">
      <!-- ================= READ-ONLY NOTICE ================= -->
      <q-banner v-if="showReadOnlyNotice" class="notice-banner" dense rounded>
        <template v-slot:avatar>
          <q-icon name="info" />
        </template>
        Core project information is read-only for your role or the 24-hour
        editing window has closed. You can still add status updates below.
      </q-banner>

      <!-- ================= STATUS UPDATES ================= -->
      <section v-if="userAccesstypes?.addStatus" class="panel-card">
        <div class="panel-header">
          <h3>Status updates</h3>
        </div>

        <div class="status-form">
          <q-input
            v-model="newUpdate.date"
            outlined
            dense
            label="Date"
            type="date"
            class="formField date-field"
          />

          <q-input
            v-model.trim="newUpdate.note"
            outlined
            dense
            placeholder="Add a dated progress note..."
            class="formField note-field"
          />

          <q-btn
            v-if="!$q.screen.lt.md"
            no-caps
            unelevated
            label="Add note"
            icon="add"
            class="primary-btn"
            :disable="isSavingStatus"
            :loading="isSavingStatus"
            @click="saveUpdate"
          />
        </div>
        <div class="attachment-upload">
          <div class="upload-label">Attachments for this update</div>
          <q-file
            v-model="newUpdate.files"
            outlined
            dense
            multiple
            counter
            use-chips
            label="Choose Files"
            class="formField"
          >
            <template v-slot:prepend>
              <q-icon name="attach_file" />
            </template>
          </q-file>
          <p class="hint-text">
            Optional. You can upload multiple files, maximum 10 MB per file.
          </p>
        </div>
        <div class="status-form-btn">
          <q-btn
            v-if="$q.screen.lt.md"
            no-caps
            unelevated
            label="Add note"
            icon="add"
            class="primary-btn"
            :disable="isSavingStatus"
            :loading="isSavingStatus"
            @click="saveUpdate"
          />
        </div>

        <!-- TIMELINE -->
        <div class="status-timeline">
          <div
            v-for="(update, i) in statusUpdates"
            :key="i"
            class="timeline-entry"
          >
            <span class="timeline-date">{{update.date ? $moment(update.date).format('DD/MM/YYYY') : '--' }}</span>
            <p class="timeline-note">{{ update.note }}</p>
            <span class="timeline-author">{{ update.author }}</span>

            <div v-if="update.attachment.fileName" class="timeline-attachment">
              <span>
                <q-icon name="description" size="18px" />
                <a
                  href="#"
                  class="attachment-link"
                  @click.prevent="
                    openProjectAttechment(update.attachment, true)
                  "
                >
                  {{ update.attachment.fileName }}
                </a>
              </span>
              <q-btn
                flat
                round
                dense
                icon="delete"
                class="delete-action"
                @click="removeProjectAttachment(update.attachment.id, 'status')"
              />

              <!-- <q-btn
                            v-if="canRemoveAttachments"
                            outline
                            dense
                            no-caps
                            size="sm"
                            label="Remove"
                            class="remove-btn"
                            @click="removeAttachment(update)"
                        /> -->
            </div>
          </div>

          <div v-if="!statusUpdates.length" class="timeline-empty">
            No status updates yet.
          </div>
        </div>
      </section>

      <!-- ================= PROJECT ATTACHMENTS ================= -->
      <section class="panel-card q-mt-md">
        <div class="panel-header">
          <h3>Project attachments</h3>
        </div>

        <div class="attachment-upload row-upload">
          <q-file
            v-model="projectFiles"
            outlined
            dense
            multiple
            use-chips
            label="Choose Files"
            class="formField upload-field"
          >
            <template v-slot:prepend>
              <q-icon name="attach_file" />
            </template>
          </q-file>

          <q-btn
            :loading="isUploading"
            :disable="isUploading"
            no-caps
            unelevated
            label="Upload"
            icon="upload"
            class="primary-btn upload-btn"
            @click="uploadProjectFiles"
          />
        </div>

        <p class="hint-text">Maximum 10 MB per file.</p>

        <div v-if="projectAttachments.length" class="attachment-list">
          <div
            v-for="(file, i) in projectAttachments"
            :key="i"
            class="attachment-row"
          >
            <q-icon name="description" size="18px" />
            <a
              class="attachment-link"
              @click="openProjectAttechment(file)"
              >{{ file.name }}</a
            >
            <q-btn
              flat
              round
              dense
              icon="delete"
              class="delete-action"
              @click="
                removeProjectAttachment(file.data?.fileObj?.id, 'project')
              "
            />
          </div>
        </div>
        <p v-else class="hint-text muted">
          No project attachments uploaded yet.
        </p>
      </section>

      <!-- ================= PRODUCTS QUOTED ================= -->
      <section v-if="userAccesstypes?.editQuoted" class="panel-card">
        <div class="panel-header">
          <h3>Products quoted</h3>
        </div>

        <template v-if="$q.screen.gt.md">
          <custom-select
            :value="form.productSearch"
            model="productSearch"
            :options="products?.map((e) => e.skuCode + '-' + e.prodDescription)"
            @change="addProductFromSearch"
            :key="products?.length"
            outlined
            dense
            label="Search product name or SKU"
            class="formField"
            :disable="false"
            :isOptionSlot="true"
          />
        </template>
        <advance-search-select
          v-else
          :value="form.productSearch"
          model="productSearch"
          :options="products?.map((e) => e.skuCode + '-' + e.prodDescription)"
          @change="addProductFromSearch"
          :key="products?.length"
          outlined
          dense
          label="Search product name or SKU "
          class="formField"
          :disable="false"
        />
        <!-- 
            <q-input
                v-model.trim="productSearch"
                outlined
                dense
                clearable
                placeholder="Search product name or SKU"
                class="formField product-search"
                @keyup.enter="addProductFromSearch"
            > 
                <template v-slot:prepend>
                    <q-icon name="search" />
                </template>
            </q-input> -->

        <div class="chips-wrapper">
          <q-chip
            v-for="(product, i) in quotedProducts"
            :key="i"
            :removable="canEditProducts"
            dense
            class="code-chip"
            @remove="quotedProducts.splice(i, 1)"
          >
            <span class="code-class">{{ product.description }}</span>
          </q-chip>
        </div>

        <div class="products-footer">
          <q-btn
            no-caps
            unelevated
            label="Save products"
            class="primary-btn"
            :disable="!canEditProducts || isItemsSavingAsync"
            :loading="isItemsSavingAsync"
            @click="saveProducts"
          />
          <!-- <span class="products-status-text">
                    {{
                        canEditProducts
                            ? 'Product quotation changes are allowed by your role.'
                            : 'Product quotation changes are allowed by your role. Project edit window has closed'
                    }}
                </span> -->
        </div>
      </section>
    </div>
    <!-- DELETE CONFIRMATION (unchanged) -->
    <q-dialog v-model="deleteDialog">
      <q-card class="delete-dialog">
        <q-card-section class="delete-dialog-header">
          <div class="delete-icon">
            <q-icon name="delete_outline" size="28px" />
          </div>
          <div>
            <h3>Delete Attachment?</h3>
          </div>
        </q-card-section>

        <q-card-section class="delete-content">
          Are you sure you want to delete attachment?
        </q-card-section>

        <q-card-actions align="right" class="delete-actions">
          <q-btn
            flat
            no-caps
            label="Cancel"
            class="cancel-btn"
            @click="deleteAttachment(false)"
          />
          <q-btn
            unelevated
            no-caps
            label="Delete Attachment"
            icon="delete"
            class="confirm-delete-btn"
            :loading="isDeleting"
            @click="deleteAttachment(true)"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import apis from "@/utilities";
import axios from "axios";

export default {
  name: "ProjectDetailPanels",

  props:['canEditCore', 'canRemoveAttachments', 'canEditProducts', "isItemSaving", "products", "userAccesstypes"  ],
  //  {
  //   // Parent se pass karein — role/24hr-window ke hisab se
  //   canEditCore: { type: Boolean, default: false },
  //   canRemoveAttachments: { type: Boolean, default: true },
  //   canEditProducts: { type: Boolean, default: true },
  //   isItemSaving: { type: Boolean, default: true },
  //   products: {
  //     type: Array,
  //     default: () => [],
  //   },
  //   userAccesstypes: {
  //     type: Object,
  //     default: () => ({}),
  //   },
  // },

  mounted() {
    const { key } = this.$route.params;
    let paramData = key.split("_");
    this.accessType = paramData[paramData.length - 1];
  },

  data() {
    return {
      showReadOnlyNotice: !this.canEditCore,
      accessType: "",
      newUpdate: {
        date: "",
        note: "",
        files: null,
      },

      statusUpdates: [
        // Yahan API se aane wala data bind karein
      ],
      form: {
        productSearch: "",
      },

      projectFiles: null,
      projectAttachments: [],

      quotedProducts: [],
      isSavingProducts: false,
      statusFiles: [],
      isSavingStatus: false,
      isUploading: false,
      userAccessTypes: {},
      isDeleting: false,
      deleteDialog: false,
      selectedAttachmentId: null,
      selectedText: "",
    };
  },

  methods: {
    removeProjectAttachment(id, text) {
      console.log("id: ", id);
      this.selectedAttachmentId = id;
      this.selectedText = text;
      this.deleteDialog = true;
    },
    async deleteAttachment(isDelete) {
      if (isDelete) {
        if (!this.selectedAttachmentId) return;
        this.isDeleting = true;
        try {
          const response = await axios.post(
            `${this.url}/${apis.DeleteProjectAttachment}/?id=${this.selectedAttachmentId}`
          );
          if (response.data.success) {
            console.log("this.statusUpdates", this.statusUpdates);
            if (this.selectedText === "project") {
              this.projectAttachments = this.projectAttachments.filter(
                (item) => item.data?.fileObj.id !== this.selectedAttachmentId
              );
            } else if (this.selectedText === "status") {
              this.statusUpdates = this.statusUpdates.filter(
                (item) => item.attachment.id !== this.selectedAttachmentId
              );
            }
            this.$q.notify({
              type: "positive",
              message: "Visit deleted successfully.",
              position: "top",
            });
            this.deleteDialog = false;
            this.selectedAttachmentId = null;
          } else {
            this.$q.notify({
              type: "negative",
              message: response.data.message || "Unable to delete project.",
              position: "top",
            });
          }
        } catch (err) {
          this.$store.dispatch("triggerUnauthorizedError", err);
          console.error(err);
          this.$q.notify({
            type: "negative",
            message: "Unable to delete project.",
            position: "top",
          });
        } finally {
          this.isDeleting = false;
        }
      } else {
        (this.isDeleting = false),
          (this.deleteDialog = false),
          (this.selectedAttachmentId = null),
          (this.selectedText = "");
      }
    },
    addNote() {
      if (!this.newUpdate.note) return;

      this.statusUpdates.unshift({
        date: this.formatDate(this.newUpdate.date),
        note: this.newUpdate.note,
        author: "Portal Administrator",
        attachment:
          this.newUpdate.files && this.newUpdate.files.length
            ? {
                name: this.newUpdate.files[0].name,
                uploadedBy: "Portal Administrator",
              }
            : null,
      });

      this.newUpdate = { date: "", note: "", files: null };
    },
    setProjectId(form, files, products) {
      console.log("form 123");

      this.form = form;
      this.quotedProducts = products.map((f) => {
        const prod = f?.split("-");
        return {
          sku: prod[0],
          description: f,
        };
      });
      this.projectAttachments = files?.map((d) => {
        return {
          name: d.fileObj.fileName,
          data: d,
        };
      });
      this.getProjectStatus();
    },
    saveProducts() {
      this.$emit("save-product", this.quotedProducts, "product");
    },

    removeAttachment(update) {
      update.attachment = null;
    },

    openAttachment(attachment) {
      console.log("attachment: ", attachment);
      // download/preview logic yahan
    },

    async uploadProjectFiles() {
      if (!this.projectFiles) return;
      this.isUploading = true;
      const filesList = await this.handleStatusUpload(this.projectFiles);

      this.$emit("files-attachment", filesList, "files");
      this.projectFiles = [];
      this.isUploading = false;
    },

    removeProjectAttachmentTemp(i) {
      this.projectAttachments.splice(i, 1);
    },

    addProductFromSearch(model, value) {
      // Search API call yahan; result select hone par push karein:

      this.quotedProducts.push({
        sku: value ? value?.split("-")[0] : "",
        description: value,
      });
      this.form[model] = "";
    },
    async handleStatusUpload(files) {
      if (files?.length > 0) {
        this.isFileUploading = true;

        let fileList = [];

        for (let fileObj of files) {
          try {
            const compressedBase64 = await this.readFileAsBase64(
              fileObj,
              15000
            );
            const resizedImage = compressedBase64
              .replace("data:", "")
              .replace(/^.+,/, "");
            if (!resizedImage) throw new Error("Empty compression result");
            fileList.push({
              fileObj,
              dataUrl: resizedImage,
              AttachmentType: fileObj.type,
            });
          } catch (err) {
            this.$store.dispatch("triggerUnauthorizedError", err);
            const newError = err.response || err;
            console.error(newError);
          }
        }

        return fileList;
      }
    },
    readFileAsBase64(file, ms = 15000) {
      console.log("file: ", file);
      const isImage = file && file.type && file.type.startsWith("image/");
      const task = isImage
        ? this.compressImage(file)
        : this.readRawFileAsBase64(file);

      return Promise.race([
        task,
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("File read timed out")), ms)
        ),
      ]);
    },

    readRawFileAsBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result); // "data:<mime>;base64,...."
        reader.onerror = () => reject(new Error("Unable to read file"));
        reader.readAsDataURL(file);
      });
    },

    async saveUpdate() {
      if (!this.newUpdate.date)
        return this.warningNotification("Date Required!");
      if (!this.newUpdate.note)
        return this.warningNotification("Note Required!");
      // const { key } = this.$route.params;
      // let paramData = key.split('_');
      this.isSavingStatus = true;
      let files = this.newUpdate.files
        ? await this.handleStatusUpload(this.newUpdate.files)
        : [];
      let postFilesList = []; // API call yahan

      if (files) {
        files.map((f) => {
          postFilesList.push({
            attachment: f.dataUrl,
            fileName: f.fileObj.name,
            contentType: f.AttachmentType,
          });
        });
      }

      const payload = {
        id: 0,
        projectId: this.form.id || null,
        statusDate: this.newUpdate.date,
        statusDetails: this.newUpdate.note,
        createdByUserId: 0,
        // createdAt: '2026-09-09T11:07:02.863Z',
        serverDate: this.form.serverDate,
        evidencePhotoRefs: postFilesList,
      };

      try {
        const response = await axios.post(
          `${this.url}/${apis.SaveStatus}`,
          payload
        );
        if (response.data.success && response.data.validationState == 0) {
          this.getProjectStatus();
          this.newUpdate.date = "";
          this.newUpdate.files = "";
          this.newUpdate.note = "";
        }

        this.globalNotification(
          response.data.message,
          response.data.validationState
        );
      } catch (err) {
        this.$store.dispatch("triggerUnauthorizedError", err);
        const newError = err.response || err;
        console.error(newError);
      }

      // setTimeout(() => {
      this.isSavingStatus = false;
      // }, 600);
    },

    async getProjectStatus() {
      // const { key } = this.$route.params;
      // let paramData = key.split('_');
      try {
        const response = await axios.get(
          `${this.url}/${apis.GetProjectStatus}?projectId=${this.form.id}`
        );
        if (response.data.success && response.data.validationState == 0) {
          const { data } = response.data;
          if (data) {
            this.statusUpdates = data.map((d) => {
              return {
                date: d.statusDate,
                note: d.statusDetails,
                attachment: d.evidencePhotoRefs[0] || {},
              };
            });
          }
        }
        // this.globalNotification(response.data.message, response.data.validationState);s
      } catch (err) {
        this.$store.dispatch("triggerUnauthorizedError", err);
        const newError = err.response || err;
        console.error(newError);
      }
    },

    formatDate(date) {
      if (!date) return this.formatDate(new Date().toISOString());
      const value = String(date).substring(0, 10);
      const parts = value.split("-");
      if (parts.length !== 3) return value;
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    },
    async compressImage(file) {
      return new Promise((resolve, reject) => {
        const maxSize = 800;
        const reader = new FileReader();

        reader.onerror = (err) => {
          console.error("FileReader failed", err);
          reject(new Error("Failed to read file: " + file.name));
        };

        reader.onload = (event) => {
          const img = new Image();

          img.onerror = (err) => {
            console.error("Image load failed for", file.name, file.type, err);
            reject(
              new Error(`Unsupported or corrupt image format: ${file.name}`)
            );
          };

          img.onload = () => {
            try {
              const width = img.width;
              const height = img.height;
              let newWidth = width;
              let newHeight = height;

              if (width > maxSize || height > maxSize) {
                if (width > height) {
                  newWidth = maxSize;
                  newHeight = Math.floor((height / width) * maxSize);
                } else {
                  newHeight = maxSize;
                  newWidth = Math.floor((width / height) * maxSize);
                }
              }

              const canvas = document.createElement("canvas");
              const ctx = canvas.getContext("2d");
              canvas.width = newWidth;
              canvas.height = newHeight;
              ctx.drawImage(img, 0, 0, newWidth, newHeight);

              const compressedBase64 = canvas.toDataURL("image/jpeg", 0.8);
              resolve(compressedBase64);
            } catch (error) {
              console.error("Canvas processing failed", error);
              reject(error);
            }
          };

          img.src = event.target.result;
        };

        reader.readAsDataURL(file);
      });
    },
    openProjectAttechment(row, isStatus) {
      const data = isStatus ? row.attachment : row.data.dataUrl;
      const type = isStatus
        ? row.contentType
        : row.data.attachmentType || row.data.AttachmentType;
      const rawBase64 = String(data || "")
        .replace(/^data:.*;base64,/, "")
        .replace(/\s+/g, "");

      const mimeType = type || "application/octet-stream";

      if (!rawBase64) {
        this.warningNotification("No attachment data found.");
        return;
      }

      try {
        const byteCharacters = atob(rawBase64);
        const byteNumbers = new Uint8Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const blob = new Blob([byteNumbers], { type: mimeType });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
      } catch (error) {
        console.error("Attachment preview failed:", error);
        this.warningNotification("The attachment could not be previewed.");
      }
    },

    // ---- Helper methods ----

    downloadFromDataUrl(dataUrl, name) {
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = name || "attachment";
      document.body.appendChild(link);
      link.click();
      link.remove();
    },

    downloadFromBlob(blob, name) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = name || "attachment";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
  },
  computed: {
    url() {
      return this.$store.getters.url;
    },
    isItemsSavingAsync () {
    return this.isItemSaving
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/quasar.scss";
/* ================= DELETE DIALOG ================= */

.delete-dialog {
  width: 420px;
  max-width: calc(100vw - 30px);
  border-radius: 14px;
}

.delete-dialog-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.delete-icon {
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  color: #c45151;
  background: #fceeee;
  border-radius: 10px;
}

.delete-dialog-header h3 {
  margin: 0;
  color: $primary;
  font-size: 17px;
}

.delete-dialog-header p {
  margin: 3px 0 0;
  color: #87969a;
  font-size: 12px;
}

.delete-content {
  color: #526a70;
  font-size: 13px;
}

.delete-content strong {
  color: $primary;
}

.delete-actions {
  padding: 12px 16px 16px;
}

.cancel-btn {
  color: #55757d;
}

.confirm-delete-btn {
  color: #fff;
  background: #c45151;
  border-radius: 7px;
}
.detail-panels {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

/* ================= NOTICE BANNER ================= */

.notice-banner {
  color: $primary;
  background: #e7f2f4;
  font-size: 13px;

  :deep(.q-icon) {
    color: $primary;
  }
}

/* ================= PANEL CARD ================= */

.panel-card {
  padding: 20px 18px;
  background: #fff;
  border: 1px solid #e3ebed;
  border-radius: 12px;
}

.panel-header {
  margin-bottom: 16px;
}

.panel-header h3 {
  margin: 0;
  color: $primary;
  font-size: 17px;
  font-weight: 700;
}

.formField :deep(.q-field__control) {
  background: #fff;
}

.hint-text {
  margin: 8px 0 0;
  color: #87969a;
  font-size: 12px;
}

.hint-text.muted {
  margin-top: 14px;
}

.primary-btn {
  min-height: 40px;
  color: #fff;
  background: $primary-gradient;
  border-radius: 6px;
  font-size: 13px;
}

/* ================= STATUS UPDATE FORM ================= */

.status-form {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  margin-bottom: 16px;
}

.status-form-btn {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  margin-bottom: 16px;
}

.attachment-upload {
  margin-bottom: 20px;
}

.upload-label {
  margin-bottom: 8px;
  color: #35525a;
  font-size: 12px;
  font-weight: 700;
}

.row-upload {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.upload-field {
  flex: 1;
  min-width: 0;
}

.upload-btn {
  flex: 0 0 auto;
}

/* ================= TIMELINE ================= */

.status-timeline {
  display: flex;
  flex-direction: column;
}

.timeline-entry {
  position: relative;
  padding: 0 0 20px 18px;
  border-left: 2px solid #e7f2f4;
}

.timeline-entry::before {
  content: "";
  position: absolute;
  left: -6px;
  top: 4px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: $primary;
}

.timeline-entry:last-child {
  border-left-color: transparent;
  padding-bottom: 0;
}

.timeline-date {
  display: block;
  color: $primary;
  font-size: 13px;
  font-weight: 700;
}

.timeline-note {
  margin: 4px 0 2px;
  color: #35525a;
  font-size: 13px;
}

.timeline-author {
  color: #87969a;
  font-size: 11px;
}

.timeline-attachment {
  // margin-top: 8px;
  // display: flex;
  // align-items: center;
  // flex-wrap: wrap;
  // gap: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  //   gap: 8px;
  padding: 8px 10px;
  background: #f9fbfb;
  border: 1px solid #edf2f3;
  border-radius: 6px;
}

.attachment-link {
  color: $primary;
  font-weight: 600;
  font-size: 13px;
  text-decoration: none;
}

.attachment-link:hover {
  text-decoration: underline;
}

.attachment-meta {
  color: #87969a;
  font-size: 11px;
}

.remove-btn {
  color: $primary;
  border-color: $primary;
  font-size: 11px;
  min-height: 26px;
}

.timeline-empty {
  color: #87969a;
  font-size: 13px;
}

/* ================= PROJECT ATTACHMENTS LIST ================= */

.attachment-list {
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.attachment-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: #f9fbfb;
  border: 1px solid #edf2f3;
  border-radius: 6px;
}
.attachment-timeLine {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: #f9fbfb;
  border: 1px solid #edf2f3;
  border-radius: 6px;
}

.attachment-row .attachment-link {
  flex: 1;
}

.delete-action {
  color: #c45151;
}

/* ================= PRODUCTS QUOTED ================= */

.product-search {
  margin-bottom: 14px;
}

.chips-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
  margin-bottom: 18px;
}

.code-chip {
  // max-width: 100%;
  height: auto;
  min-height: 28px;
  color: $primary;
  background: #e7f2f4;

  :deep(.q-chip__content) {
    white-space: normal;
    word-break: break-word;
    line-height: 1.3;
    padding: 4px 0;
  }
}

.code-class {
  white-space: normal;
  word-break: break-word;
  font-size: 12px;
  font-weight: 600;
}

.products-footer {
  display: flex;
  align-items: center;
  justify-content: end;
  gap: 14px;
  flex-wrap: wrap;
}

.products-status-text {
  color: #87969a;
  font-size: 12px;
}

/* ============================================================
   MOBILE
   ============================================================ */

@media (max-width: 599px) {
  .row-upload {
    flex-direction: column;
  }

  .upload-btn {
    width: 100%;
  }

  .code-chip {
    width: fit-content;
  }

  .products-footer {
    .primary-btn {
      width: 100%;
    }

    flex-direction: column;
    align-items: flex-end;
  }

  .upload-field {
    flex: 1;
    min-width: 100%;
  }
}

/* ============================================================
   TABLET+ (>= 641px)
   ============================================================ */

@media (min-width: 641px) {
  .status-form {
    grid-template-columns: auto 1fr auto;
    align-items: center;
  }

  .status-form-btn {
    // grid-template-columns: auto 1fr auto;
    // align-items: end;
    display: flex;
    width: 100%;
    justify-content: flex-end;
  }

  .date-field {
    min-width: 170px;
  }
}
</style>